import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset password. The link may be invalid or expired.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Password Reset Complete!</h2>
            <p className="text-slate-600 mb-8">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link to="/login">
              <Button 
                className="w-full h-12 bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm font-semibold tracking-wide uppercase"
                data-testid="reset-success-login-btn"
              >
                Go to Login
              </Button>
            </Link>
          </div>
        </div>

        {/* Right Panel */}
        <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="text-center text-white">
              <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 className="w-14 h-14 text-white" />
              </div>
              <h2 className="font-heading text-4xl font-bold mb-4">Success!</h2>
              <p className="text-white/80 text-lg max-w-md mx-auto">
                Your account is now secured with your new password.
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-greek-blue mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-greek-blue rounded-sm flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-greek-blue">AHEPA Family</h1>
                <p className="text-xs text-slate-500">Scholarship Program</p>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Create New Password</h2>
            <p className="text-slate-600 mt-2">
              Please enter a strong password for your account.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm" data-testid="reset-error-message">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue pr-12"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue pr-12"
                  data-testid="reset-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm font-semibold tracking-wide uppercase"
              data-testid="reset-password-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <p className="text-center mt-8 text-slate-600">
            Remember your password?{' '}
            <Link to="/login" className="text-greek-blue font-semibold hover:underline" data-testid="reset-login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
              <Lock className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-4xl font-bold mb-4">Secure Your Account</h2>
            <p className="text-white/80 text-lg max-w-md mx-auto">
              Choose a strong password to keep your scholarship account protected.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
